import { filterEmpty } from '@fox/atoms/utils/filter-empty';
import { IconButton } from '@fox/atoms/src/icon-button/index';
import { strIsNullOrEmpty, tryParseJSONObject } from '@fox/atoms/utils/string';
import { TextField } from '@fox/atoms/src/text-field/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { AutoDataGrid } from '@fox/data-grid/src/auto-data-grid/index';
import { OverflowTextCell } from '@fox/data-grid/src/data-grid-table/components/cell-item';
import { FlexColumn, FlexRowSmallSpaced } from '@fox/layout/src/flex';
import { HalfPadded } from '@fox/layout/src/style';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { RequiredAsterisk } from '../../../required-asterisk/required-asterisk';
import { alignDictionary, SpecialButton } from '../../transaction-block/styled-components';
import { QuestionBlockMetaColumns, QuestionBlockMetadata, QuestionBlockProps } from '../contracts';

export interface IRenderDataTableQuestionProps {
	id: string;
	fieldProps: QuestionBlockProps;
}

type DataTableAnswers = DataTableRowAnswer[];
type DataTableRowAnswer = { rowId: string } & Record<string, string>;

export const RenderDataTableQuestion = ({ id, fieldProps }: IRenderDataTableQuestionProps) => {
	const { getAnswer, answerQuestion } = useFormRuntimeContext();
	const fieldMeta = tryParseJSONObject<QuestionBlockMetadata>(fieldProps.metadataJson, {});
	const questionName = fieldProps.name;
	const questionRequired = fieldMeta.required;
	const columns: QuestionBlockMetaColumns = (fieldMeta?.columns ?? []).filter(c => !c.deletedAt);
	const currentAnswer = getAnswer(questionName);
	const [currentAnswerSelections, setCurrentAnswerSelections] = useState(
		filterEmpty(tryParseJSONObject<DataTableRowAnswer[]>(currentAnswer?.answer?.answerJson, []))
	);
	const theme = useTheme();

	const style = fieldProps.style;
	const background = style?.backgroundColor ?? theme.colors.secondary;
	const color = style?.color ?? '#ffffff';
	const buttonsAlignment = fieldProps.attributes?.horizontalAlign;

	const icons: (row: { rowId: string }) => React.ReactNode[] = row => {
		return [
			<IconButton
				size="small"
				iconName="Delete"
				onClick={() => {
					setCurrentAnswerSelections(prev => {
						const newAnswers: DataTableAnswers = prev.filter(a => a.rowId !== row.rowId);
						answerQuestion({
							questionName,
							answer: {
								answerJson: JSON.stringify(newAnswers)
							}
						});
						return newAnswers;
					});
				}}
			/>
		];
	};

	const actionIcons = { icons, width: 50 };

	const handleChange = (val: string, rowId: string, columnId: string) => {
		setCurrentAnswerSelections(prev => {
			const newAnswers: DataTableAnswers = prev.map(a => (a.rowId === rowId ? { ...a, [columnId]: val } : a));
			answerQuestion({
				questionName,
				answer: {
					answerJson: JSON.stringify(newAnswers)
				}
			});
			return newAnswers;
		});
	};

	const calculateColumnTotal = (columnId: string) => {
		return String(
			currentAnswerSelections.reduce((acc, cur) => {
				const val = cur[columnId];
				const number = val && !isNaN(Number(val)) ? Number(val) : 0;
				return acc + number;
			}, 0)
		);
	};
	return (
		<>
			<AutoDataGrid<DataTableRowAnswer>
				data={currentAnswerSelections}
				id={`form-data-table-${id}`}
				toolbar={[
					<FlexColumn>
						<FlexRowSmallSpaced style={{ alignSelf: buttonsAlignment ? alignDictionary[buttonsAlignment] : 'start' }}>
							<SpecialButton
								type="button"
								$borderRadius={fieldProps.borderRadius}
								$fontFamily={style?.fontFamily}
								$fontSize={style?.fontSize}
								$fontWeight={style?.fontWeight}
								$fontStyle={style?.fontStyle}
								$textDecoration={style?.textDecoration}
								$borderColor={style?.borderColor}
								$borderSize={style?.borderSize}
								$hoverBackgroundColour={style?.hoverBackgroundColour}
								$background={background}
								$color={color}
								onClick={() => {
									setCurrentAnswerSelections(prev => {
										const emptyColumnAnswers = columns.reduce((acc, c) => {
											acc[c.id] = '';
											return acc;
										}, {});
										const newAnswers: DataTableAnswers = [...prev, { rowId: nanoid(), ...emptyColumnAnswers }];
										answerQuestion({
											questionName,
											answer: {
												answerJson: JSON.stringify(newAnswers)
											}
										});
										return newAnswers;
									});
								}}
							>
								Add Row
							</SpecialButton>
						</FlexRowSmallSpaced>
					</FlexColumn>
				]}
				getRowKey={a => a.rowId}
				loading={false}
				buildActionIcons={actionIcons}
				columns={columns.map((column, index) => ({
					dataIndex: column.id,
					title: (
						<Typography>
							{column.name}
							{column.required && questionRequired ? <RequiredAsterisk /> : null}
						</Typography>
					),
					hideable: false,
					canDrag: false,
					initialWidth: 125,
					columnFooter:
						column.type === 'number' ? (
							<HalfPadded>
								<OverflowTextCell>{calculateColumnTotal(column.id)}</OverflowTextCell>
							</HalfPadded>
						) : undefined,
					columnKey: column.id,
					render: (value, record) => (
						<InputCell
							key={`${record.rowId}-${column.id}`}
							value={value}
							record={record}
							column={column}
							handleChange={(val: string) => handleChange(val, record.rowId, column.id)}
							questionRequired={!!questionRequired}
						/>
					)
				}))}
			/>
		</>
	);
};

const InputCell = ({
	value,
	column,
	handleChange,
	questionRequired
}: {
	value: string | undefined;
	record: DataTableRowAnswer;
	column: QuestionBlockMetaColumns[0];
	handleChange: (val: string) => void;
	questionRequired: boolean;
}) => {
	const [hasBlurred, setHasBlurred] = useState(false);
	return (
		<TextField
			size="small"
			value={value}
			variant="standard"
			status={hasBlurred && strIsNullOrEmpty(value) && questionRequired && column.required ? 'error' : undefined}
			onBlur={() => setHasBlurred(true)}
			InputProps={{ disableUnderline: true }}
			onChange={e => {
				const value = e.target.value;
				if (column.type === 'number') {
					if (/^-?\d*\.?\d*$/.test(value)) {
						handleChange(value);
					}
				} else {
					handleChange(value);
				}
			}}
			type={column.type === 'number' ? 'number' : 'text'}
		/>
	);
};
